import { useState, useEffect, useRef } from "react";
import SvgMapper from "../../../features/common/SvgMapper";
import "../../Style.css";

const CustomCalendar = ({ value, onChange, minDate, name, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const calendarRef = useRef(null);
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const selectedDate = value ? new Date(value + "T00:00:00") : null;

  useEffect(() => {
    if (selectedDate && !isNaN(selectedDate.getTime())) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const updatePopupPosition = () => {
    if (inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      let top = inputRect.bottom + 4; 
      let left = inputRect.left;
      const estimatedPopupHeight = 280;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      
      if (spaceBelow < estimatedPopupHeight && spaceAbove > estimatedPopupHeight) {
        top = inputRect.top - estimatedPopupHeight - 4;
      }
      
      const popupWidth = 280;
      if (left + popupWidth > window.innerWidth) {
        left = window.innerWidth - popupWidth - 10;
      }
      
      if (left < 10) {
        left = 10;
      }
      
      setPopupPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        updatePopupPosition();
      }, 0);
      
      const handleScroll = () => {
        updatePopupPosition();
      };
      
      const handleResize = () => {
        updatePopupPosition();
      };
      
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
      
      document.addEventListener("scroll", handleScroll, true);
      
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
        document.removeEventListener("scroll", handleScroll, true);
        clearTimeout(timeoutId);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString + "T00:00:00");
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = formatDateForInput(newDate);
    onChange({ target: { name: name, value: dateString } });
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    const dateString = formatDateForInput(today);
    onChange({ target: { name: name, value: dateString } });
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({ target: { name: name, value: "" } });
    setIsOpen(false);
  };

  const isToday = (day) => {
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return (
      checkDate.getDate() === selectedDate.getDate() &&
      checkDate.getMonth() === selectedDate.getMonth() &&
      checkDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isDisabled = (day) => {
    if (!minDate) return false;
    
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const minDateObj = new Date(minDate + "T00:00:00");
    
    checkDate.setHours(0, 0, 0, 0);
    minDateObj.setHours(0, 0, 0, 0);
    
    return checkDate < minDateObj;
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  return (
    <div ref={calendarRef} className={`custom-calendar-container ${className}`}>
      <div
        ref={inputRef}
        className="custom-calendar-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{formatDateForDisplay(value) || "Select date..."}</span>
        <span className="custom-calendar-icon">
          {SvgMapper.calendar("w-5 h-5 text-gray-500")}
        </span>
      </div>

      {isOpen && (
        <div 
          ref={popupRef}
          className="custom-calendar-popup"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
          }}
        >
          <div className="custom-calendar-header">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="custom-calendar-nav-button"
            >
              ‹
            </button>
            <div className="custom-calendar-month-year">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="custom-calendar-nav-button"
            >
              ›
            </button>
          </div>

          <div className="custom-calendar-weekdays">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div key={index} className="custom-calendar-weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="custom-calendar-days">
            {days.map((day, index) => {
              if (day === null) {
                return <div key={index} className="custom-calendar-day-empty"></div>;
              }

              const selected = isSelected(day);
              const today = isToday(day);
              const dayDisabled = isDisabled(day);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => !dayDisabled && handleDateSelect(day)}
                  disabled={dayDisabled}
                  className={`custom-calendar-day ${
                    selected ? "custom-calendar-day-selected" : ""
                  } ${today ? "custom-calendar-day-today" : ""} ${
                    dayDisabled ? "custom-calendar-day-disabled" : ""
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="custom-calendar-footer">
            <button
              type="button"
              onClick={handleClear}
              className="custom-calendar-footer-button"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="custom-calendar-footer-button"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCalendar;

